import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import localStorageService from '../../utils/localStorageService';
import {
  AcademicCapIcon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const CertificateForm = ({ initialData, isEditing = false, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  
  // États pour les données
  const [students, setStudents] = useState([]);
  const [trainers, setTrainers] = useState([]);

  // État pour le formulaire
  const [formData, setFormData] = useState({
    studentId: '',
    studentName: '',
    certificateType: 'diploma',
    certificateName: '',
    issueDate: '',
    expirationDate: '',
    score: '',
    instructorId: '',
    instructorName: '',
    remarks: ''
  });

  // État pour l'option "Pas d'expiration"
  const [noExpiry, setNoExpiry] = useState(false);

  // Charger les étudiants et formateurs depuis localStorage (real data only)
  useEffect(() => {
    // No mock data - using only real students and trainers
    const studentsData = localStorageService.getAllItems('students');
    const trainersData = localStorageService.getAllItems('trainers');
    
    setStudents(studentsData);
    setTrainers(trainersData);
  }, []);

  // Initialiser le formulaire avec les données existantes pour l'édition
  useEffect(() => {
    if (initialData && isEditing) {
      const hasExpiry = initialData.expirationDate && initialData.expirationDate !== 'never';
      setNoExpiry(!hasExpiry);
      
      setFormData({
        studentId: initialData.studentId || '',
        studentName: initialData.studentName || '',
        certificateType: initialData.certificateType || 'diploma',
        certificateName: initialData.certificateName || '',
        issueDate: initialData.issueDate || '',
        expirationDate: hasExpiry ? initialData.expirationDate : '',
        score: initialData.score || '',
        instructorId: initialData.instructorId || '',
        instructorName: initialData.instructorName || '',
        remarks: initialData.remarks || ''
      });
    }
  }, [initialData, isEditing]);

  // Mutation pour ajouter une certification
  const addCertificationMutation = useMutation(
    async (newCertification) => {
      try {
        // Générer un ID unique pour la nouvelle certification
        const certId = `CERT${Date.now().toString().slice(-4)}`;
        
        // Créer l'objet de certification complet
        const certification = {
          id: certId,
          ...newCertification,
          status: newCertification.issueDate ? 'issued' : 'pending'
        };
        
        // Ajouter à localStorage
        localStorageService.addItem('certifications', certification);
        
        return {
          message: 'Certification ajoutée avec succès',
          certification
        };
      } catch (error) {
        console.error('Erreur lors de l\'ajout de la certification:', error);
        throw new Error('Erreur lors de l\'ajout de la certification');
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('certifications');
        onSuccess && onSuccess();
      }
    }
  );

  // Mutation pour modifier une certification
  const updateCertificationMutation = useMutation(
    async (updatedCertification) => {
      try {
        // Créer l'objet de certification complet
        const certification = {
          ...initialData,
          ...updatedCertification,
          status: updatedCertification.issueDate ? 'issued' : 'pending'
        };
        
        // Mettre à jour dans localStorage
        localStorageService.updateItem('certifications', certification);
        
        return {
          message: 'Certification mise à jour avec succès',
          certification
        };
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la certification:', error);
        throw new Error('Erreur lors de la mise à jour de la certification');
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('certifications');
        onSuccess && onSuccess();
      }
    }
  );

  // Gestion du formulaire
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // Si on change l'étudiant, mettre à jour l'ID et le nom
    if (name === 'studentId') {
      const selectedStudent = students.find(s => s.id === value);
      setFormData(prev => ({
        ...prev,
        studentId: value,
        studentName: selectedStudent ? selectedStudent.name : ''
      }));
      return;
    }
    
    // Si on change le formateur, mettre à jour l'ID et le nom
    if (name === 'instructorId') {
      const selectedTrainer = trainers.find(t => t.id === value);
      setFormData(prev => ({
        ...prev,
        instructorId: value,
        instructorName: selectedTrainer ? selectedTrainer.name : ''
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Gérer la soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Préparer les données avec la gestion de l'expiration
    const submitData = {
      ...formData,
      expirationDate: noExpiry ? 'never' : formData.expirationDate
    };
    
    if (isEditing && initialData) {
      updateCertificationMutation.mutate(submitData);
    } else {
      addCertificationMutation.mutate(submitData);
    }
  };

  // Vérifier si une mutation est en cours
  const isLoading = addCertificationMutation.isLoading || updateCertificationMutation.isLoading;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 w-full"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 flex items-center">
          <AcademicCapIcon className="h-6 w-6 mr-2" />
          {isEditing ? 'Modifier la Certification' : 'Nouvelle Certification'}
        </h2>
        <button 
          onClick={onClose}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-full transition-colors"
        >
          <XMarkIcon className="h-5 w-5 text-slate-900 dark:text-slate-50" />
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Informations de l'étudiant */}
          <div className="md:col-span-2">
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">Nom de l'Étudiant</label>
            <select
              name="studentId"
              value={formData.studentId}
              onChange={handleFormChange}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Sélectionner un étudiant</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.name} ({student.studentCode})
                </option>
              ))}
            </select>
          </div>
          
          {/* Type de certification */}
          <div>
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">Type de Certification</label>
            <select
              name="certificateType"
              value={formData.certificateType}
              onChange={handleFormChange}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-50"
              required
            >
              <option value="diploma">Diplôme</option>
              <option value="certification">Certification</option>
              <option value="attestation">Attestation</option>
            </select>
          </div>
          
          {/* Nom de la certification */}
          <div>
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">Titre</label>
            <input 
              type="text"
              name="certificateName"
              value={formData.certificateName}
              onChange={handleFormChange}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-50"
              required
            />
          </div>
          
          {/* Dates */}
          <div>
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">Date de Délivrance</label>
            <input 
              type="date"
              name="issueDate"
              value={formData.issueDate}
              onChange={handleFormChange}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-50"
            />
          </div>
          
          <div>
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">Date d'Expiration</label>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="noExpiry"
                  checked={noExpiry}
                  onChange={(e) => {
                    setNoExpiry(e.target.checked);
                    if (e.target.checked) {
                      setFormData(prev => ({ ...prev, expirationDate: '' }));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-600 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="noExpiry" className="ml-2 text-sm text-slate-700 dark:text-slate-300">
                  Valide à vie (pas d'expiration)
                </label>
              </div>
              {!noExpiry && (
                <input 
                  type="date"
                  name="expirationDate"
                  value={formData.expirationDate}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
              {noExpiry && (
                <div className="w-full px-3 py-2 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
                  ✓ Cette certification sera valide à vie
                </div>
              )}
            </div>
          </div>
          
          {/* Score */}
          <div>
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">Score (sur 100)</label>
            <input 
              type="number"
              name="score"
              value={formData.score}
              onChange={handleFormChange}
              min="0"
              max="100"
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-50"
            />
          </div>
          
          {/* Informations du formateur */}
          <div className="md:col-span-2">
            <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">Nom du Formateur</label>
            <select
              name="instructorId"
              value={formData.instructorId}
              onChange={handleFormChange}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Sélectionner un formateur</option>
              {trainers.map(trainer => (
                <option key={trainer.id} value={trainer.id}>
                  {trainer.name} - {trainer.specialty}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Remarques */}
        <div>
          <label className="block text-slate-600 dark:text-slate-400 text-sm mb-1">Remarques</label>
          <textarea
            name="remarks"
            value={formData.remarks}
            onChange={handleFormChange}
            rows="3"
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-50"
          ></textarea>
        </div>
        
        <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-600">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            disabled={isLoading}
          >
            Annuler
          </button>
          
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all flex items-center space-x-2"
          >
            {isLoading && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
            <span>{isEditing ? 'Mettre à jour' : 'Ajouter'}</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default CertificateForm;